/**
 * SourceProviderGroup Component
 *
 * Provider-first model: collapsible header representing a sending provider.
 * Shows aggregate stats, effective authorization status, and provider category.
 * Matched providers (from known_providers) show a verified badge.
 */

import { useState } from "react";
import { ChevronDown, ChevronRight, Server, Mail, CheckCircle2, AlertTriangle, XCircle, BadgeCheck, Loader2 } from "lucide-react";
import type { Source } from "@/lib/api";

interface SourceProviderGroupProps {
  providerName: string;
  providerId: string | null;
  providerCategory: string | null;
  effectiveStatus: "authorized" | "pending_review" | "unauthorized";
  sources: Source[];
  expanded: boolean;
  onToggle: () => void;
  selectedCount: number;
  onSelectAll: (selected: boolean) => void;
  showCheckbox: boolean;
  onAuthorize?: (providerId: string) => Promise<void>;
  enforcementReadiness?: "ready" | "issues" | "blocking" | null;
}

// Helper to check if a source is a forwarder
function isForwarder(source: Source): boolean {
  const forwarderTypes = ["forwarder", "mailing_list", "trusted_forwarder", "arc_forwarder"];
  if (source.source_type && forwarderTypes.includes(source.source_type)) {
    return true;
  }
  return source.forwarding_detected === true;
}

// Get the effective DKIM rate for a source (auth rate for forwarders, alignment rate for direct)
function getEffectiveDkimRate(source: Source): number {
  if (isForwarder(source) && source.dkim_auth_pass_rate != null) {
    return source.dkim_auth_pass_rate;
  }
  return source.dkim_pass_rate || 0;
}

// Category display labels
const CATEGORY_LABELS: Record<string, string> = {
  esp: "ESP",
  crm: "CRM",
  marketing: "Marketing",
  transactional: "Transactional",
  infrastructure: "Infrastructure",
  unknown: "Unknown",
};

// Status config for effective provider status
const STATUS_CONFIG = {
  authorized: {
    icon: CheckCircle2,
    className: "bg-emerald-100 text-emerald-700",
    label: "Authorized",
  },
  pending_review: {
    icon: AlertTriangle,
    className: "bg-orange-100 text-orange-700",
    label: "Pending",
  },
  unauthorized: {
    icon: XCircle,
    className: "bg-red-100 text-red-700",
    label: "Unauthorized",
  },
};

export function SourceProviderGroup({
  providerName,
  providerId,
  providerCategory,
  effectiveStatus,
  sources,
  expanded,
  onToggle,
  selectedCount,
  onSelectAll,
  showCheckbox,
  onAuthorize,
  enforcementReadiness,
}: SourceProviderGroupProps) {
  const [authorizing, setAuthorizing] = useState(false);
  // Calculate aggregate stats with volume-weighted averages
  const totalVolume = sources.reduce((sum, s) => sum + s.total_message_count, 0);

  // Weight SPF/DKIM rates by message volume for accurate group averages
  const weightedSpfRate = totalVolume > 0
    ? sources.reduce((sum, s) => sum + (s.spf_pass_rate || 0) * s.total_message_count, 0) / totalVolume
    : 0;
  const weightedDkimRate = totalVolume > 0
    ? sources.reduce((sum, s) => sum + getEffectiveDkimRate(s) * s.total_message_count, 0) / totalVolume
    : 0;

  const allSelected = selectedCount === sources.length && sources.length > 0;
  const someSelected = selectedCount > 0 && selectedCount < sources.length;

  const statusConfig = STATUS_CONFIG[effectiveStatus];
  const StatusIcon = statusConfig.icon;

  return (
    <div
      className={`bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer border-l-4 ${
        effectiveStatus === "authorized"
          ? "border-l-emerald-400"
          : effectiveStatus === "pending_review"
            ? "border-l-amber-400"
            : "border-l-red-500"
      }`}
      onClick={onToggle}
    >
      <div className="px-4 py-3 flex items-center gap-3">
        {/* Checkbox for bulk selection */}
        {showCheckbox && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onSelectAll(!allSelected);
            }}
            className="flex-shrink-0"
          >
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={() => {}}
              className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer"
            />
          </div>
        )}

        {/* Expand/Collapse Icon */}
        <div className="flex-shrink-0 text-slate-400">
          {expanded ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
        </div>

        {/* Provider Icon */}
        <div className="flex-shrink-0 p-2 bg-white rounded-lg border border-slate-200">
          <Server className="w-4 h-4 text-slate-500" />
        </div>

        {/* Provider Name, Status, and Metadata */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-800 truncate">
              {providerName}
            </span>
            {/* Matched provider badge */}
            {providerId && (
              <span title="Matched to known provider"><BadgeCheck className="w-4 h-4 text-blue-500 flex-shrink-0" /></span>
            )}
            {/* IP count */}
            <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs font-medium rounded-full">
              {sources.length} IP{sources.length !== 1 ? "s" : ""}
            </span>
            {/* Provider category tag */}
            {providerCategory && (
              <span className="hidden sm:inline-flex px-2 py-0.5 bg-slate-100 text-slate-500 text-xs font-medium rounded-full border border-slate-200">
                {CATEGORY_LABELS[providerCategory] || providerCategory}
              </span>
            )}
            {/* Effective authorization status */}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.className}`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </span>
            {/* Enforcement readiness badge */}
            {enforcementReadiness && enforcementReadiness !== "ready" && (
              <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                enforcementReadiness === "blocking"
                  ? "bg-red-50 text-red-600 border border-red-200"
                  : "bg-amber-50 text-amber-600 border border-amber-200"
              }`}>
                {enforcementReadiness === "blocking" ? (
                  <XCircle className="w-3 h-3" />
                ) : (
                  <AlertTriangle className="w-3 h-3" />
                )}
                {enforcementReadiness === "blocking" ? "Blocking" : "Issues"}
              </span>
            )}
          </div>
        </div>

        {/* Authorize Button */}
        {effectiveStatus !== "authorized" && providerId && onAuthorize && (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              setAuthorizing(true);
              try {
                await onAuthorize(providerId);
              } finally {
                setAuthorizing(false);
              }
            }}
            disabled={authorizing}
            className="flex-shrink-0 px-3 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
          >
            {authorizing ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Authorizing...
              </>
            ) : (
              "Authorize"
            )}
          </button>
        )}

        {/* Aggregate Stats */}
        <div className="hidden md:flex items-center gap-6 text-sm">
          {/* Total Volume */}
          <div className="flex items-center gap-1.5 text-slate-500">
            <Mail className="w-4 h-4" />
            <span className="font-medium text-slate-700">
              {totalVolume.toLocaleString()}
            </span>
          </div>

          {/* SPF Rate (volume-weighted) */}
          <div className="flex items-center gap-1">
            <span className="text-slate-500">SPF</span>
            <span
              className={`font-medium ${
                weightedSpfRate >= 0.8
                  ? "text-emerald-700"
                  : weightedSpfRate >= 0.5
                  ? "text-amber-600"
                  : "text-red-600"
              }`}
            >
              {(weightedSpfRate * 100).toFixed(0)}%
            </span>
          </div>

          {/* DKIM Rate (volume-weighted) */}
          <div className="flex items-center gap-1">
            <span className="text-slate-500">DKIM</span>
            <span
              className={`font-medium ${
                weightedDkimRate >= 0.8
                  ? "text-emerald-700"
                  : weightedDkimRate >= 0.5
                  ? "text-amber-600"
                  : "text-red-600"
              }`}
            >
              {(weightedDkimRate * 100).toFixed(0)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
